import './IngestionObservabilityPage.css';

import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Tab, Tabs, TablePagination, TextField, Typography } from '@mui/material';
import { Button as AntButton, ConfigProvider, DatePicker, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ReceptionLoadScheduler,
  ReceptionQuery,
  receptionQueryKey,
} from './receptionLoadScheduler';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import { dmsAntdTheme } from '../../config/antdTheme';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { SkeletonBlock, SkeletonKpiRail, SkeletonRegion, SkeletonTable } from '../common/Skeleton/Skeleton';
import { useCapabilityAccess } from '../../hooks/useCapabilityAccess';
import { ingestionObservabilityService } from '../../services/ingestionObservabilityService';
import { getAllRtusData } from '../../services/rtuService';
import {
  IngestionAuditRowResponse,
  IngestionHealthResponse,
  IngestionLatencyMetricsResponse,
  IngestionPagedResponse,
  IngestionQuarantineRowResponse,
  IngestionRuntimeMetricsResponse,
  RtuFileActivityRowResponse,
} from '../../types/IngestionObservability';
import { RTU } from '../../types/RTU';
import { useTranslation } from 'react-i18next';

interface ObservabilitySnapshot {
  health: IngestionHealthResponse;
  runtime: IngestionRuntimeMetricsResponse;
  latency: IngestionLatencyMetricsResponse;
  loadedAt: string;
}

type InvestigationPanelAvailability = 'ready' | 'unavailable' | 'error';
type PageTab = 'dataReception' | 'technicalDiagnostics';

const SOURCE_IDENTITY_MAX_LENGTH = 36;

interface InvestigationPanelState {
  availability: InvestigationPanelAvailability;
  message: string | null;
}

interface InvestigationPanelsState {
  fileActivity: InvestigationPanelState;
  auditRows: InvestigationPanelState;
  quarantineRows: InvestigationPanelState;
}

interface EvidenceTableQuery {
  page: number;
  size: number;
  search: string;
}

interface ObservabilityFilterQuery {
  rtuId: string;
  from: string;
  to: string;
}

interface RtuLookupRow {
  id: number;
  name: string;
  field: string;
}

interface RtuSearchOption {
  value: string;
  label: string;
  rtuId: number;
  name: string;
  field: string;
}

const RTU_OPTIONS_EMPTY_LIMIT = 25;
const RTU_OPTIONS_SEARCH_LIMIT = 20;

const normalizeRtuName = (rtu: RTU): string => rtu.name?.trim() || `RTU ${rtu.id}`;

const toRtuLookupRow = (rtu: RTU): RtuLookupRow => ({
  id: rtu.id,
  name: normalizeRtuName(rtu),
  field: rtu.field?.trim() || '',
});

const toRtuSearchOption = (row: RtuLookupRow): RtuSearchOption => ({
  value: String(row.id),
  label: `${row.name} (${row.id})`,
  rtuId: row.id,
  name: row.name,
  field: row.field,
});

const numberFormatter = new Intl.NumberFormat();

const formatMetric = (value: number | null | undefined) => numberFormatter.format(value ?? 0);
const formatPercent = (value: number) => `${Math.round(value * 1000) / 10}%`;

const formatHealthClass = (value: string | null | undefined) => {
  const normalized = String(value ?? '').toUpperCase();
  if (normalized === 'INFRA') {
    return 'Connectivity';
  }
  if (normalized === 'DATA_QUALITY') {
    return 'Data Quality';
  }
  if (normalized === 'HEALTHY') {
    return 'Healthy';
  }
  return value || 'Status';
};

const formatTimestamp = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const healthChipColor = (status: string) => {
  if (status === 'UP') {
    return 'success';
  }
  if (status === 'DEGRADED') {
    return 'warning';
  }
  return 'error';
};

const statusChipColor = (status: string | null | undefined) => {
  const normalized = String(status ?? '').toUpperCase();
  if (normalized.includes('FAILED') || normalized === 'FAILED' || normalized === 'QUARANTINED') {
    return 'error';
  }
  if (normalized.includes('RETRY') || normalized.includes('PENDING')) {
    return 'warning';
  }
  if (normalized.includes('COMPLETED') || normalized.includes('SUCCEEDED') || normalized === 'ACCEPTED') {
    return 'success';
  }
  return 'default';
};

const formatStageName = (value: string) =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatEventStatusLabel = (value: string | null | undefined) => {
  const normalized = String(value ?? '').toUpperCase();
  const mapped: Record<string, string> = {
    ACCEPTED: 'Accepted',
    QUARANTINED: 'Quarantined',
    FAILED: 'Failed',
    REJECTED: 'Rejected',
    STATE_UPDATE_COMPLETED: 'Saved',
    STATE_UPDATE_FAILED: 'Failed',
    STATE_UPDATE_RETRYING: 'Retrying',
    STATE_UPDATE_STALE_REJECTED: 'Older Update Rejected',
    PROJECTION_COMPLETED: 'Saved',
    PROJECTION_FAILED: 'Failed',
  };

  if (mapped[normalized]) {
    return mapped[normalized];
  }

  return value ? formatStageName(value) : '—';
};

const formatRouteLabel = (value: string | null | undefined) => {
  const normalized = String(value ?? '').toUpperCase();
  if (normalized === 'MQTT') {
    return 'MQTT';
  }
  if (normalized === 'HTTP') {
    return 'HTTP';
  }
  if (normalized === 'MANUAL') {
    return 'Manual';
  }
  return value ? formatStageName(value) : '—';
};

const isQuarantineRedriveEligible = (row: IngestionQuarantineRowResponse) => {
  const status = String(row.status ?? '').toUpperCase();
  return row.downloadAvailable && (status === 'PENDING' || status === 'REDRIVE_FAILED');
};

const shortenIdentity = (value: string, maxLength = SOURCE_IDENTITY_MAX_LENGTH) => {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 1)}…`;
};

const formatSourceFileDisplay = (row: RtuFileActivityRowResponse) => {
  if (row.sourceFilename) {
    return row.sourceFilename;
  }
  if (row.correlationId) {
    return shortenIdentity(row.correlationId);
  }
  return '—';
};

const formatSourceFileTooltip = (row: RtuFileActivityRowResponse) =>
  row.sourceFilename || row.correlationId || undefined;

const TruncatedCellValue = ({
  value,
  className,
  display,
}: {
  value: string;
  className?: string;
  display?: string;
}) => (
  <div
    className={`ingestion-observability-truncate${className ? ` ${className}` : ''}`}
    title={value}
  >
    {display ?? value}
  </div>
);

const shouldShowHealthClassChip = (status: string, healthClass: string | null | undefined) => {
  const normalizedStatus = String(status ?? '').toUpperCase();
  const normalizedClass = String(healthClass ?? '').toUpperCase();
  if (normalizedStatus === 'UP' && (normalizedClass === 'HEALTHY' || !normalizedClass)) {
    return false;
  }
  return Boolean(healthClass);
};

const shouldShowHealthReason = (status: string, reason: string | null | undefined) => {
  const normalizedStatus = String(status ?? '').toUpperCase();
  const trimmedReason = String(reason ?? '').trim();
  if (!trimmedReason) {
    return false;
  }
  if (normalizedStatus === 'UP') {
    return trimmedReason.toLowerCase() !== 'healthy';
  }
  return true;
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="ingestion-observability-stat">
    <span className="ingestion-observability-stat-label">{label}</span>
    <span className="ingestion-observability-stat-value">{value}</span>
  </div>
);

const SectionCard = ({
  title,
  subtitle,
  dense = false,
  children,
}: {
  title: string;
  subtitle?: string;
  dense?: boolean;
  children: React.ReactNode;
}) => (
  <section className={`ingestion-observability-card flat-card${dense ? ' ingestion-observability-card--dense' : ''}`}>
    <div className="ingestion-observability-card-header">
      <Typography variant="h6" className="ingestion-observability-card-title">
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" className="ingestion-observability-card-subtitle">
          {subtitle}
        </Typography>
      ) : null}
    </div>
    <div className="ingestion-observability-card-body">{children}</div>
  </section>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="ingestion-observability-empty">{message}</div>
);

const DEFAULT_PANEL_STATE: InvestigationPanelState = {
  availability: 'ready',
  message: null,
};

const DEFAULT_PANELS_STATE: InvestigationPanelsState = {
  fileActivity: DEFAULT_PANEL_STATE,
  auditRows: DEFAULT_PANEL_STATE,
  quarantineRows: DEFAULT_PANEL_STATE,
};

const DEFAULT_TABLE_QUERY: EvidenceTableQuery = {
  page: 0,
  size: 10,
  search: '',
};

const DEFAULT_OBSERVABILITY_FILTER_QUERY: ObservabilityFilterQuery = {
  rtuId: '',
  from: '',
  to: '',
};

const toIsoInstant = (value: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toISOString();
};

const createEmptyPage = <T,>(size: number): IngestionPagedResponse<T> => ({
  items: [],
  page: 0,
  size,
  totalElements: 0,
  totalPages: 0,
  hasNext: false,
});

const createPanelState = (
  reason: any,
  unavailableMessage: string,
  errorMessage: string
): InvestigationPanelState => {
  const statusCode = reason?.response?.status;
  const rawMessage = String(reason?.response?.data?.message || reason?.message || '').trim();

  if (statusCode === 403 || statusCode === 404) {
    return {
      availability: 'unavailable',
      message: unavailableMessage,
    };
  }

  if (/timeout|timed out|network error|econnaborted/i.test(rawMessage)) {
    return {
      availability: 'error',
      message: errorMessage,
    };
  }

  if (rawMessage && !/status code|axios|request failed/i.test(rawMessage)) {
    return {
      availability: 'error',
      message: rawMessage,
    };
  }

  return {
    availability: 'error',
    message: errorMessage,
  };
};

const SectionMessage = ({ state }: { state: InvestigationPanelState }) => {
  if (!state.message) {
    return null;
  }

  return (
    <Alert severity={state.availability === 'error' ? 'warning' : 'info'}>
      {state.message}
    </Alert>
  );
};

const IngestionObservabilityPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAccessInternalObservability } = useCapabilityAccess();
  const canShowTechnicalDiagnostics =
    canAccessInternalObservability && FEATURE_FLAGS.technicalDiagnostics;
  const [activeTab, setActiveTab] = useState<PageTab>('dataReception');
  const [snapshot, setSnapshot] = useState<ObservabilitySnapshot | null>(null);
  const [panelStates, setPanelStates] = useState<InvestigationPanelsState>(DEFAULT_PANELS_STATE);
  const [fileActivityQuery, setFileActivityQuery] = useState<EvidenceTableQuery>(DEFAULT_TABLE_QUERY);
  const [auditQuery, setAuditQuery] = useState<EvidenceTableQuery>(DEFAULT_TABLE_QUERY);
  const [quarantineQuery, setQuarantineQuery] = useState<EvidenceTableQuery>(DEFAULT_TABLE_QUERY);
  const [quarantineSearchInput, setQuarantineSearchInput] = useState('');
  const [activeFilters, setActiveFilters] = useState<ObservabilityFilterQuery>(DEFAULT_OBSERVABILITY_FILTER_QUERY);
  const [rtus, setRtus] = useState<RTU[]>([]);
  const [rtuSearch, setRtuSearch] = useState('');
  const [fileActivityPage, setFileActivityPage] = useState<IngestionPagedResponse<RtuFileActivityRowResponse>>(
    createEmptyPage(DEFAULT_TABLE_QUERY.size)
  );
  const [auditPage, setAuditPage] = useState<IngestionPagedResponse<IngestionAuditRowResponse>>(
    createEmptyPage(DEFAULT_TABLE_QUERY.size)
  );
  const [quarantinePage, setQuarantinePage] = useState<IngestionPagedResponse<IngestionQuarantineRowResponse>>(
    createEmptyPage(DEFAULT_TABLE_QUERY.size)
  );
  const [receptionLoading, setReceptionLoading] = useState(true);
  const [receptionRefreshing, setReceptionRefreshing] = useState(false);
  const [receptionError, setReceptionError] = useState<string | null>(null);
  const [receptionLoadedAt, setReceptionLoadedAt] = useState<string | null>(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsRefreshing, setDiagnosticsRefreshing] = useState(false);
  const [diagnosticsError, setDiagnosticsError] = useState<string | null>(null);
  const [diagnosticsLoaded, setDiagnosticsLoaded] = useState(false);
  const [downloadingCorrelationId, setDownloadingCorrelationId] = useState<string | null>(null);
  const [selectedQuarantineIds, setSelectedQuarantineIds] = useState<Set<number>>(new Set());
  const [redriveReason, setRedriveReason] = useState('');
  const [redrivingIds, setRedrivingIds] = useState<Set<number>>(new Set());
  const [redriveNotice, setRedriveNotice] = useState<{ severity: 'success' | 'warning' | 'error'; message: string } | null>(null);
  const diagnosticsEvidenceRequestIdRef = useRef(0);
  const diagnosticsAccessGenerationRef = useRef(0);
  const diagnosticsGateRef = useRef(canShowTechnicalDiagnostics);
  const receptionHasLoadedRef = useRef(false);
  const receptionDisplayedKeyRef = useRef<string | null>(null);
  const [receptionDisplayedKey, setReceptionDisplayedKey] = useState<string | null>(null);
  const receptionSchedulerRef = useRef<ReceptionLoadScheduler<
    IngestionPagedResponse<RtuFileActivityRowResponse>
  > | null>(null);
  diagnosticsGateRef.current = canShowTechnicalDiagnostics;

  useEffect(() => {
    if (canShowTechnicalDiagnostics) {
      return;
    }

    diagnosticsAccessGenerationRef.current += 1;
    diagnosticsEvidenceRequestIdRef.current += 1;
    if (activeTab !== 'dataReception') {
      setActiveTab('dataReception');
    }
    setSnapshot(null);
    setDiagnosticsLoaded(false);
    setDiagnosticsLoading(false);
    setDiagnosticsRefreshing(false);
    setDiagnosticsError(null);
  }, [activeTab, canShowTechnicalDiagnostics]);

  const normalizedFilters = useMemo(
    () => ({
      rtuId: activeFilters.rtuId.trim() || undefined,
      from: toIsoInstant(activeFilters.from),
      to: toIsoInstant(activeFilters.to),
    }),
    [activeFilters]
  );

  const rtuLookupRows = useMemo(() => rtus.map(toRtuLookupRow), [rtus]);

  const rtuById = useMemo(
    () => new Map(rtuLookupRows.map((row) => [row.id, row])),
    [rtuLookupRows]
  );

  const rtuSearchOptions = useMemo<RtuSearchOption[]>(() => {
    const query = rtuSearch.trim().toLowerCase();
    if (!query) {
      return rtuLookupRows.slice(0, RTU_OPTIONS_EMPTY_LIMIT).map(toRtuSearchOption);
    }
    return rtuLookupRows
      .filter(
        (row) =>
          row.name.toLowerCase().includes(query) ||
          row.field.toLowerCase().includes(query) ||
          String(row.id).includes(query)
      )
      .slice(0, RTU_OPTIONS_SEARCH_LIMIT)
      .map(toRtuSearchOption);
  }, [rtuLookupRows, rtuSearch]);

  const dateRangeValue = useMemo((): [Dayjs, Dayjs] | null => {
    const from = activeFilters.from ? dayjs(activeFilters.from) : null;
    const to = activeFilters.to ? dayjs(activeFilters.to) : null;
    if (from?.isValid() && to?.isValid()) {
      return [from, to];
    }
    return null;
  }, [activeFilters.from, activeFilters.to]);

  const formatRtuDisplay = useCallback(
    (rtuId: string | null | undefined) => {
      if (!rtuId) {
        return '—';
      }
      const numericId = Number(rtuId);
      const row = Number.isFinite(numericId) ? rtuById.get(numericId) : undefined;
      if (row) {
        return `${row.name} (${rtuId})`;
      }
      return rtuId;
    },
    [rtuById]
  );

  /**
   * Bug 3150: Recovery Queue only. Header-derived rtuId is resolved exactly like
   * formatRtuDisplay; when no header identity exists at all, falls back to the
   * filename-derived identifier (resolved to a real RTU name when it matches a
   * known RTU, otherwise shown as the raw identifier without claiming it is
   * canonical). Kept separate from the shared formatRtuDisplay, which the audit
   * and file-activity tables also use and which must not change behavior here.
   */
  const formatQuarantineIdentity = useCallback(
    (row: IngestionQuarantineRowResponse): { display: string; fromFilename: boolean } => {
      if (row.rtuId) {
        return { display: formatRtuDisplay(row.rtuId), fromFilename: false };
      }
      if (row.filenameDerivedRtuId) {
        return { display: formatRtuDisplay(row.filenameDerivedRtuId), fromFilename: true };
      }
      return { display: '—', fromFilename: false };
    },
    [formatRtuDisplay]
  );

  const eligibleQuarantineIds = useMemo(
    () => quarantinePage.items.filter(isQuarantineRedriveEligible).map((row) => row.id),
    [quarantinePage.items]
  );
  const selectedQuarantineCount = selectedQuarantineIds.size;
  const allVisibleQuarantineSelected =
    eligibleQuarantineIds.length > 0 && eligibleQuarantineIds.every((id) => selectedQuarantineIds.has(id));

  const receptionQuery = useMemo<ReceptionQuery>(
    () => ({
      rtuId: normalizedFilters.rtuId,
      from: normalizedFilters.from,
      to: normalizedFilters.to,
      page: fileActivityQuery.page,
      size: fileActivityQuery.size,
      search: fileActivityQuery.search || undefined,
    }),
    [fileActivityQuery.page, fileActivityQuery.search, fileActivityQuery.size, normalizedFilters.from, normalizedFilters.rtuId, normalizedFilters.to]
  );
  const receptionQueryId = receptionQueryKey(receptionQuery);
  const receptionQueryRef = useRef(receptionQuery);
  receptionQueryRef.current = receptionQuery;
  const translateRef = useRef(t);
  translateRef.current = t;

  // Data Reception loading/polling is deliberately independent of technical diagnostics state
  // (see receptionLoadScheduler): one outstanding request per mounted page, latest changed query kept, 30 s after
  // settlement. This bounds HTTP requests from this page instance only; it cannot cancel server-side SQL.
  useEffect(() => {
    const scheduler = new ReceptionLoadScheduler<IngestionPagedResponse<RtuFileActivityRowResponse>>({
      fetch: (query) => ingestionObservabilityService.fetchReceptionFileActivityPage(query),
      onStart: () => {
        if (receptionHasLoadedRef.current) {
          setReceptionRefreshing(true);
        } else {
          setReceptionLoading(true);
        }
      },
      onSuccess: (page, _query, key) => {
        setFileActivityPage(page);
        setPanelStates((previous) => ({
          ...previous,
          fileActivity: DEFAULT_PANEL_STATE,
        }));
        setReceptionError(null);
        setReceptionLoadedAt(new Date().toISOString());
        receptionHasLoadedRef.current = true;
        receptionDisplayedKeyRef.current = key;
        setReceptionDisplayedKey(key);
      },
      onError: (loadError, query, key) => {
        const translate = translateRef.current;
        const panelState = createPanelState(
          loadError as any,
          translate('observability.errors.evidenceUnavailable'),
          translate('observability.errors.evidenceLoadFailed')
        );
        // Content may only stay on screen while it still answers the current query; a failure for a
        // different query must not leave the previous query's rows presented as its results.
        if (!receptionHasLoadedRef.current || receptionDisplayedKeyRef.current !== key) {
          setFileActivityPage(createEmptyPage(query.size));
          setPanelStates((previous) => ({
            ...previous,
            fileActivity: panelState,
          }));
          receptionDisplayedKeyRef.current = key;
          setReceptionDisplayedKey(key);
        }
        setReceptionError(panelState.message);
      },
      onIdle: () => {
        setReceptionLoading(false);
        setReceptionRefreshing(false);
      },
    });
    receptionSchedulerRef.current = scheduler;
    return () => {
      scheduler.dispose();
      receptionSchedulerRef.current = null;
    };
  }, []);

  useEffect(() => {
    receptionSchedulerRef.current?.request(receptionQueryRef.current);
  }, [receptionQueryId]);

  const loadDiagnosticsEvidence = useCallback(async (background = false) => {
    if (!diagnosticsGateRef.current) {
      return;
    }

    const requestId = ++diagnosticsEvidenceRequestIdRef.current;
    if (background) {
      setDiagnosticsRefreshing(true);
    } else {
      setDiagnosticsLoading(true);
    }

    const commonFilters = {
      rtuId: normalizedFilters.rtuId,
      from: normalizedFilters.from,
      to: normalizedFilters.to,
    };

    const [auditRowsResult, quarantineRowsResult] = await Promise.allSettled([
      ingestionObservabilityService.fetchAuditRowsPage({
        ...commonFilters,
        page: auditQuery.page,
        size: auditQuery.size,
        search: auditQuery.search || undefined,
      }),
      ingestionObservabilityService.fetchQuarantineRowsPage({
        ...commonFilters,
        page: quarantineQuery.page,
        size: quarantineQuery.size,
        search: quarantineQuery.search || undefined,
      }),
    ]);

    if (!diagnosticsGateRef.current || requestId !== diagnosticsEvidenceRequestIdRef.current) {
      return;
    }

    setAuditPage(auditRowsResult.status === 'fulfilled' ? auditRowsResult.value : createEmptyPage(auditQuery.size));
    setQuarantinePage(
      quarantineRowsResult.status === 'fulfilled' ? quarantineRowsResult.value : createEmptyPage(quarantineQuery.size)
    );
    setPanelStates((previous) => ({
      ...previous,
      auditRows:
        auditRowsResult.status === 'fulfilled'
          ? DEFAULT_PANEL_STATE
          : createPanelState(
              auditRowsResult.reason,
              t('observability.errors.evidenceUnavailable'),
              t('observability.errors.evidenceLoadFailed')
            ),
      quarantineRows:
        quarantineRowsResult.status === 'fulfilled'
          ? DEFAULT_PANEL_STATE
          : createPanelState(
              quarantineRowsResult.reason,
              t('observability.errors.evidenceUnavailable'),
              t('observability.errors.evidenceLoadFailed')
            ),
    }));
    setDiagnosticsLoading(false);
    setDiagnosticsRefreshing(false);
  }, [auditQuery.page, auditQuery.search, auditQuery.size, normalizedFilters.from, normalizedFilters.rtuId, normalizedFilters.to, quarantineQuery.page, quarantineQuery.search, quarantineQuery.size, t]);

  const loadSnapshot = useCallback(async (background = false) => {
    if (!diagnosticsGateRef.current) {
      return;
    }

    const accessGeneration = diagnosticsAccessGenerationRef.current;
    if (background) {
      setDiagnosticsRefreshing(true);
    } else {
      setDiagnosticsLoading(true);
    }

    try {
      const [health, runtime, latency] = await Promise.all([
        ingestionObservabilityService.fetchHealth(),
        ingestionObservabilityService.fetchRuntimeMetrics(),
        ingestionObservabilityService.fetchLatencyMetrics(),
      ]);

      if (!diagnosticsGateRef.current || accessGeneration !== diagnosticsAccessGenerationRef.current) {
        return;
      }

      setSnapshot({
        health,
        runtime,
        latency,
        loadedAt: new Date().toISOString(),
      });
      setDiagnosticsError(null);
      setDiagnosticsLoaded(true);
    } catch (loadError: any) {
      const panelState = createPanelState(
        loadError,
        t('observability.errors.evidenceUnavailable'),
        t('observability.errors.loadFailed')
      );
      setDiagnosticsError(panelState.message);
    } finally {
      setDiagnosticsLoading(false);
      setDiagnosticsRefreshing(false);
    }
  }, [t]);

  const loadDiagnostics = useCallback(async (background = false) => {
    await Promise.all([loadSnapshot(background), loadDiagnosticsEvidence(background)]);
  }, [loadDiagnosticsEvidence, loadSnapshot]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuarantineQuery((previous) => {
        if (previous.search === quarantineSearchInput) {
          return previous;
        }
        return {
          ...previous,
          page: 0,
          search: quarantineSearchInput,
        };
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [quarantineSearchInput]);

  useEffect(() => {
    void getAllRtusData()
      .then((data) => setRtus(Array.isArray(data) ? (data as RTU[]) : []))
      .catch(() => setRtus([]));
  }, []);

  useEffect(() => {
    if (!canShowTechnicalDiagnostics || !diagnosticsLoaded) {
      return undefined;
    }
    const refreshHandle = window.setInterval(() => {
      if (diagnosticsGateRef.current) {
        void loadDiagnostics(true);
      }
    }, 30000);
    return () => window.clearInterval(refreshHandle);
  }, [canShowTechnicalDiagnostics, diagnosticsLoaded, loadDiagnostics]);

  useEffect(() => {
    if (!canShowTechnicalDiagnostics || !diagnosticsLoaded || activeTab !== 'technicalDiagnostics') {
      return;
    }
    void loadDiagnosticsEvidence(true);
  }, [activeTab, canShowTechnicalDiagnostics, diagnosticsLoaded, loadDiagnosticsEvidence]);

  useEffect(() => {
    setSelectedQuarantineIds((previous) => {
      if (previous.size === 0) {
        return previous;
      }
      const visibleIds = new Set(quarantinePage.items.map((row) => row.id));
      const next = new Set<number>();
      previous.forEach((id) => {
        if (visibleIds.has(id)) {
          next.add(id);
        }
      });
      return next.size === previous.size ? previous : next;
    });
  }, [quarantinePage.items]);

  const handleDownload = useCallback(async (correlationId: string) => {
    setDownloadingCorrelationId(correlationId);
    try {
      await ingestionObservabilityService.downloadInvestigationFile(correlationId);
    } catch (downloadError: any) {
      const panelState = createPanelState(
        downloadError,
        t('observability.errors.evidenceUnavailable'),
        t('observability.errors.downloadFailed')
      );
      setReceptionError(panelState.message);
    } finally {
      setDownloadingCorrelationId(null);
    }
  }, [t]);

  const setRedriveBusy = useCallback((id: number, busy: boolean) => {
    setRedrivingIds((previous) => {
      const next = new Set(previous);
      if (busy) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const toggleQuarantineSelection = useCallback((id: number, selected: boolean) => {
    setSelectedQuarantineIds((previous) => {
      const next = new Set(previous);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const toggleVisibleQuarantineSelection = useCallback((selected: boolean) => {
    setSelectedQuarantineIds((previous) => {
      const next = new Set(previous);
      eligibleQuarantineIds.forEach((id) => {
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  }, [eligibleQuarantineIds]);

  const handleSingleRedrive = useCallback(async (row: IngestionQuarantineRowResponse) => {
    if (!isQuarantineRedriveEligible(row)) {
      return;
    }
    setRedriveBusy(row.id, true);
    setRedriveNotice(null);
    try {
      const result = await ingestionObservabilityService.redriveQuarantineRow(row.id, redriveReason.trim() || undefined);
      setRedriveNotice({
        severity: result.submitted ? 'success' : 'warning',
        message:
          result.message ||
          (result.submitted ? t('observability.redrive.submitted') : t('observability.redrive.notSubmitted')),
      });
      toggleQuarantineSelection(row.id, false);
      await loadDiagnosticsEvidence(true);
      await loadSnapshot(true);
    } catch (redriveError: any) {
      setRedriveNotice({
        severity: redriveError?.response?.status === 403 ? 'error' : 'warning',
        message:
          redriveError?.response?.data?.message ||
          redriveError?.message ||
          t('observability.redrive.failed'),
      });
    } finally {
      setRedriveBusy(row.id, false);
    }
  }, [loadDiagnosticsEvidence, loadSnapshot, redriveReason, setRedriveBusy, t, toggleQuarantineSelection]);

  const handleBulkRedrive = useCallback(async () => {
    const ids = Array.from(selectedQuarantineIds);
    if (ids.length === 0) {
      return;
    }
    const reason = redriveReason.trim();
    if (!reason) {
      setRedriveNotice({
        severity: 'warning',
        message: t('observability.redrive.bulkReasonRequired'),
      });
      return;
    }

    setRedrivingIds(new Set(ids));
    setRedriveNotice(null);
    try {
      const result = await ingestionObservabilityService.redriveQuarantineRows(ids, reason);
      setRedriveNotice({
        severity: result.failed > 0 ? 'warning' : 'success',
        message: t('observability.redrive.bulkResult', {
          submitted: result.submitted,
          skipped: result.skipped,
          failed: result.failed,
        }),
      });
      setSelectedQuarantineIds(new Set());
      setRedriveReason('');
      await loadDiagnosticsEvidence(true);
      await loadSnapshot(true);
    } catch (redriveError: any) {
      setRedriveNotice({
        severity: redriveError?.response?.status === 403 ? 'error' : 'warning',
        message:
          redriveError?.response?.data?.message ||
          redriveError?.message ||
          t('observability.redrive.bulkFailed'),
      });
    } finally {
      setRedrivingIds(new Set());
    }
  }, [loadDiagnosticsEvidence, loadSnapshot, redriveReason, selectedQuarantineIds, t]);

  const latencyRows = useMemo(
    () => Object.entries(snapshot?.latency ?? {}).sort(([left], [right]) => left.localeCompare(right)),
    [snapshot]
  );
  const showDataReceptionTab = activeTab === 'dataReception' || !canShowTechnicalDiagnostics;
  const showTechnicalDiagnosticsTab =
    canShowTechnicalDiagnostics && activeTab === 'technicalDiagnostics';

  const handleDiagnosticsTabOpen = useCallback(() => {
    if (canShowTechnicalDiagnostics && !diagnosticsLoaded) {
      void loadDiagnostics();
    }
  }, [canShowTechnicalDiagnostics, diagnosticsLoaded, loadDiagnostics]);

  const handleSearchChange =
    (setter: React.Dispatch<React.SetStateAction<EvidenceTableQuery>>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setter((previous) => ({
        ...previous,
        page: 0,
        search: value,
      }));
    };

  const handlePageChange =
    (setter: React.Dispatch<React.SetStateAction<EvidenceTableQuery>>) =>
    (_event: unknown, nextPage: number) => {
      setter((previous) => ({
        ...previous,
        page: nextPage,
      }));
    };

  const handleRowsPerPageChange =
    (setter: React.Dispatch<React.SetStateAction<EvidenceTableQuery>>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setter((previous) => ({
        ...previous,
        page: 0,
        size: Number(event.target.value),
      }));
    };

  const resetEvidencePages = () => {
    setFileActivityQuery((previous) => ({ ...previous, page: 0 }));
    setAuditQuery((previous) => ({ ...previous, page: 0 }));
    setQuarantineQuery((previous) => ({ ...previous, page: 0 }));
  };

  const handleRtuFilterChange = (value: string | undefined) => {
    const rtuId = value ?? '';
    if (activeFilters.rtuId === rtuId) {
      return;
    }
    setActiveFilters((previous) => ({
      ...previous,
      rtuId,
    }));
    resetEvidencePages();
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const from = dates?.[0]?.isValid() ? dates[0].toISOString() : '';
    const to = dates?.[1]?.isValid() ? dates[1].toISOString() : '';
    if (activeFilters.from === from && activeFilters.to === to) {
      return;
    }
    setActiveFilters((previous) => ({
      ...previous,
      from,
      to,
    }));
    resetEvidencePages();
  };

  const clearFilters = () => {
    const hasActiveFilters = Boolean(activeFilters.rtuId || activeFilters.from || activeFilters.to);
    setRtuSearch('');
    if (!hasActiveFilters) {
      return;
    }
    setActiveFilters(DEFAULT_OBSERVABILITY_FILTER_QUERY);
    resetEvidencePages();
  };

  const showReceptionSkeleton = receptionLoading && !receptionLoadedAt;
  // Rows on screen belong to an earlier query while the current one is still loading.
  const receptionResultsStale = receptionDisplayedKey !== null && receptionDisplayedKey !== receptionQueryId;

  return (
    <div className="ingestion-observability-page">
      {/* The breadcrumb already shows "Data Reception" as the page title
          (ACT-UI-011); this hidden h1 keeps a real heading in the
          accessibility tree without repeating it on screen. */}
      <h1 className="dms-visually-hidden">{t('observability.title')}</h1>

      <div className="ingestion-observability-tabs-row">
        {canAccessInternalObservability ? (
          <Tabs
            value={activeTab}
            onChange={(_event, nextTab: PageTab) => {
              if (nextTab === 'technicalDiagnostics' && !canShowTechnicalDiagnostics) {
                return;
              }
              setActiveTab(nextTab);
              if (nextTab === 'technicalDiagnostics') {
                handleDiagnosticsTabOpen();
              }
            }}
            className="ingestion-observability-tabs"
          >
            <Tab value="dataReception" label={t('observability.tabs.dataReception')} />
            {canShowTechnicalDiagnostics ? (
              <Tab value="technicalDiagnostics" label={t('observability.tabs.technicalDiagnostics')} />
            ) : null}
          </Tabs>
        ) : (
          <div className="ingestion-observability-tabs-spacer" aria-hidden="true" />
        )}

        <div className="ingestion-observability-tabs-meta">
          <Typography variant="body2" className="ingestion-observability-page-updated">
            {t('observability.lastUpdated', { value: formatTimestamp(receptionLoadedAt ?? snapshot?.loadedAt) })}
          </Typography>
          {receptionRefreshing || diagnosticsRefreshing ? <CircularProgress size={16} /> : null}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => {
              receptionSchedulerRef.current?.request(receptionQueryRef.current);
              if (canShowTechnicalDiagnostics && diagnosticsLoaded) {
                void loadDiagnostics(true);
              }
            }}
            className="ingestion-observability-refresh"
          >
            {t('observability.actions.refresh')}
          </Button>
        </div>
      </div>

      {receptionError && panelStates.fileActivity.availability === 'ready' ? (
        <Alert severity="warning">{receptionError}</Alert>
      ) : null}
      {diagnosticsError && showTechnicalDiagnosticsTab ? <Alert severity="warning">{diagnosticsError}</Alert> : null}

      <>
          {showDataReceptionTab || showTechnicalDiagnosticsTab ? (
            <ConfigProvider theme={dmsAntdTheme}>
              <div className="ingestion-observability-filter-bar">
                <Select
                  allowClear
                  showSearch
                  suffixIcon={null}
                  className="ingestion-observability-filter-rtu-select"
                  placeholder={t('observability.filters.rtuSearch')}
                  options={rtuSearchOptions}
                  value={activeFilters.rtuId || undefined}
                  searchValue={rtuSearch}
                  filterOption={false}
                  optionRender={(option) => (
                    <div className="ingestion-observability-rtu-option">
                      <span>{(option.data as RtuSearchOption).name} ({(option.data as RtuSearchOption).rtuId})</span>
                      {(option.data as RtuSearchOption).field ? (
                        <small>{(option.data as RtuSearchOption).field}</small>
                      ) : null}
                    </div>
                  )}
                  onSearch={setRtuSearch}
                  onChange={(value) => handleRtuFilterChange(value as string | undefined)}
                  onClear={() => {
                    setRtuSearch('');
                    handleRtuFilterChange(undefined);
                  }}
                />
                <DatePicker.RangePicker
                  showTime
                  allowClear
                  className="ingestion-observability-filter-range"
                  placeholder={[t('observability.filters.dateRange'), t('observability.filters.dateRange')]}
                  value={dateRangeValue}
                  onChange={(dates) => handleDateRangeChange(dates as [Dayjs | null, Dayjs | null] | null)}
                />
                <AntButton onClick={clearFilters}>{t('observability.filters.clear')}</AntButton>
              </div>
            </ConfigProvider>
          ) : null}

          {showDataReceptionTab ? (
            showReceptionSkeleton ? (
              <SkeletonRegion label={t('observability.loading', { defaultValue: 'Loading' })}>
                <section className="ingestion-observability-card flat-card">
                  <div className="ingestion-observability-card-header">
                    <SkeletonBlock width="9rem" height="1.1rem" />
                  </div>
                  <SkeletonTable columns={[1.3, 0.8, 1, 0.7, 0.9, 1, 1.4, 1, 0.6]} rows={8} />
                </section>
              </SkeletonRegion>
            ) : (
            <>
              <SectionCard title={t('observability.sections.fileActivity')}>
            <div className="ingestion-observability-table-toolbar">
              <TextField
                size="small"
                value={fileActivityQuery.search}
                onChange={handleSearchChange(setFileActivityQuery)}
                placeholder={t('search.placeholder')}
                className="ingestion-observability-search"
              />
            </div>
            {receptionResultsStale ? (
              <Typography
                variant="body2"
                role="status"
                className="ingestion-observability-muted ingestion-observability-stale-note"
              >
                {t('observability.updatingResults', {
                  defaultValue: 'Updating results — the rows below are for the previous filters.',
                })}
              </Typography>
            ) : null}
            {panelStates.fileActivity.availability !== 'ready' ? (
              <SectionMessage state={panelStates.fileActivity} />
            ) : fileActivityPage.items.length === 0 ? (
              <EmptyState message={t('observability.empty.fileActivity')} />
            ) : (
              <div
                className={`ingestion-observability-table-wrap${receptionResultsStale ? ' ingestion-observability-table-wrap--stale' : ''}`}
                aria-busy={receptionResultsStale}
              >
                <table className="ingestion-observability-table">
                  <thead>
                    <tr>
                      <th>{t('observability.tables.createdAt')}</th>
                      <th>{t('observability.tables.status')}</th>
                      <th>{t('observability.tables.stateUpdate')}</th>
                      <th>{t('observability.tables.rtuId')}</th>
                      <th>{t('observability.tables.fileType')}</th>
                      <th>{t('observability.tables.route')}</th>
                      <th>{t('observability.tables.sourceFile')}</th>
                      <th>{t('observability.tables.error')}</th>
                      <th>{t('observability.tables.download')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileActivityPage.items.map((row) => (
                      <tr key={row.activityId}>
                        <td>
                          <div>{formatTimestamp(row.receivedAt)}</div>
                          {row.processedAt ? (
                            <div className="ingestion-observability-muted">
                              {t('observability.tables.processedAt', { defaultValue: 'Processed' })}:{' '}
                              {formatTimestamp(row.processedAt)}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <span className="ingestion-observability-chip-cell">
                            <Chip size="small" color={statusChipColor(row.outcome)} label={formatEventStatusLabel(row.outcome)} />
                          </span>
                        </td>
                        <td>
                          <span className="ingestion-observability-chip-cell">
                            {row.stateUpdateStatus ? (
                              <Chip
                                size="small"
                                color={statusChipColor(row.stateUpdateStatus)}
                                label={formatEventStatusLabel(row.stateUpdateStatus)}
                              />
                            ) : (
                              <Typography variant="body2" className="ingestion-observability-muted">—</Typography>
                            )}
                          </span>
                        </td>
                        <td>{formatRtuDisplay(row.rtuId)}</td>
                        <td>
                          <div>{row.fileType || '—'}</div>
                          {row.payloadSizeBytes ? (
                            <div className="ingestion-observability-muted">{formatMetric(row.payloadSizeBytes)} bytes</div>
                          ) : null}
                        </td>
                        <td>{formatRouteLabel(row.route)}</td>
                        <td>
                          {formatSourceFileTooltip(row) ? (
                            <TruncatedCellValue
                              value={formatSourceFileTooltip(row) as string}
                              display={formatSourceFileDisplay(row)}
                            />
                          ) : (
                            <div>—</div>
                          )}
                          {row.stage ? (
                            <div className="ingestion-observability-muted">{row.stage}</div>
                          ) : null}
                        </td>
                        <td>
                          <div>{row.errorCode || row.errorMessage || row.safeReason || '—'}</div>
                          {row.errorCode && (row.errorMessage || row.safeReason) ? (
                            <div className="ingestion-observability-muted">{row.errorMessage || row.safeReason}</div>
                          ) : null}
                        </td>
                        <td>
                          {row.downloadAvailable && row.correlationId ? (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={downloadingCorrelationId === row.correlationId ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                              onClick={() => void handleDownload(row.correlationId as string)}
                              disabled={downloadingCorrelationId === row.correlationId}
                            >
                              {t('observability.actions.downloadFile')}
                            </Button>
                          ) : (
                            <Typography variant="body2" className="ingestion-observability-muted">—</Typography>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <TablePagination
              component="div"
              count={fileActivityPage.totalElements}
              page={fileActivityQuery.page}
              onPageChange={handlePageChange(setFileActivityQuery)}
              rowsPerPage={fileActivityQuery.size}
              onRowsPerPageChange={handleRowsPerPageChange(setFileActivityQuery)}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="ingestion-observability-pagination"
            />
          </SectionCard>
            </>
            )
          ) : null}

          {showTechnicalDiagnosticsTab ? (
            <div className="ingestion-observability-diagnostics">
              {diagnosticsLoading && !snapshot ? (
                <SkeletonRegion label={t('observability.loading', { defaultValue: 'Loading' })}>
                  <section className="ingestion-observability-card flat-card ingestion-observability-card--dense">
                    <div className="ingestion-observability-card-header">
                      <SkeletonBlock width="7rem" height="1.1rem" />
                    </div>
                    <SkeletonKpiRail rows={4} />
                  </section>
                  <div className="ingestion-observability-grid">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <section className="ingestion-observability-card flat-card ingestion-observability-card--dense" key={index}>
                        <div className="ingestion-observability-card-header">
                          <SkeletonBlock width="6rem" height="1.1rem" />
                        </div>
                        <SkeletonKpiRail rows={4} />
                      </section>
                    ))}
                  </div>
                  <section className="ingestion-observability-card flat-card ingestion-observability-card--dense">
                    <div className="ingestion-observability-card-header">
                      <SkeletonBlock width="6rem" height="1.1rem" />
                    </div>
                    <SkeletonTable columns={[2, 1, 1, 1, 1, 1]} rows={5} />
                  </section>
                  <div className="ingestion-observability-card-grid">
                    <section className="ingestion-observability-card flat-card ingestion-observability-card--dense">
                      <div className="ingestion-observability-card-header">
                        <SkeletonBlock width="7rem" height="1.1rem" />
                      </div>
                      <SkeletonTable columns={[1.2, 1, 0.8, 0.7, 1, 1]} rows={5} />
                    </section>
                    <section className="ingestion-observability-card flat-card ingestion-observability-card--dense">
                      <div className="ingestion-observability-card-header">
                        <SkeletonBlock width="7rem" height="1.1rem" />
                      </div>
                      <SkeletonTable columns={[0.4, 1.2, 0.8, 0.7, 1, 0.8]} rows={5} />
                    </section>
                  </div>
                </SkeletonRegion>
              ) : null}
              {snapshot ? (
                <>
              <SectionCard title={t('observability.sections.overview')} dense>
                <div className="ingestion-observability-status-row">
                  <Chip color={healthChipColor(snapshot.health.status)} label={snapshot.health.status} />
                  {shouldShowHealthClassChip(snapshot.health.status, snapshot.health.healthClass) ? (
                    <Chip variant="outlined" size="small" label={formatHealthClass(snapshot.health.healthClass)} />
                  ) : null}
                  {shouldShowHealthReason(snapshot.health.status, snapshot.health.reason) ? (
                    <Typography variant="body2" className="ingestion-observability-status-reason">
                      {snapshot.health.reason}
                    </Typography>
                  ) : null}
                </div>
                <Box className="ingestion-observability-section-gap" />
                <div className="ingestion-observability-stat-list">
                  <Stat label={t('observability.metrics.failureRatio')} value={formatPercent(snapshot.health.failureRatio)} />
                  <Stat label={t('observability.metrics.samples')} value={formatMetric(snapshot.health.samples)} />
                  <Stat label={t('observability.metrics.queueDepth')} value={formatMetric(snapshot.health.queueDepth)} />
                  <Stat label={t('observability.metrics.activeWorkers')} value={formatMetric(snapshot.health.activeWorkers)} />
                </div>
              </SectionCard>

              <div className="ingestion-observability-grid">
                <SectionCard title={t('observability.sections.throughput')} dense>
                  <div className="ingestion-observability-stat-list">
                    <Stat label={t('observability.metrics.received')} value={formatMetric(snapshot.runtime.received)} />
                    <Stat label={t('observability.metrics.accepted')} value={formatMetric(snapshot.runtime.accepted)} />
                    <Stat label={t('observability.metrics.duplicates')} value={formatMetric(snapshot.runtime.duplicates)} />
                    <Stat label={t('observability.metrics.failed')} value={formatMetric(snapshot.runtime.failed)} />
                    <Stat label={t('observability.metrics.quarantineWrites')} value={formatMetric(snapshot.runtime.quarantineWrites)} />
                    <Stat label={t('observability.metrics.redriveAttempts')} value={formatMetric(snapshot.runtime.redriveAttempts)} />
                  </div>
                </SectionCard>

                <SectionCard title={t('observability.sections.dispatch')} dense>
                  <div className="ingestion-observability-stat-list">
                    <Stat label={t('observability.metrics.dispatchSubmitted')} value={formatMetric(snapshot.runtime.dispatchSubmitted)} />
                    <Stat label={t('observability.metrics.dispatchRejected')} value={formatMetric(snapshot.runtime.dispatchRejected)} />
                    <Stat label={t('observability.metrics.dispatchThrottledCount')} value={formatMetric(snapshot.runtime.dispatchThrottledCount)} />
                    <Stat label={t('observability.metrics.dispatchQueueDepth')} value={formatMetric(snapshot.runtime.dispatchQueueDepth)} />
                    <Stat label={t('observability.metrics.dispatchActiveWorkers')} value={formatMetric(snapshot.runtime.dispatchActiveWorkers)} />
                    <Stat label={t('observability.metrics.dispatchThrottledWaitMsTotal')} value={formatMetric(snapshot.runtime.dispatchThrottledWaitMsTotal)} />
                  </div>
                </SectionCard>

                <SectionCard title={t('observability.sections.mqtt')} dense>
                  <div className="ingestion-observability-stat-list">
                    <Stat label={t('observability.metrics.mqttConnected')} value={snapshot.runtime.mqttConnected ? t('observability.values.connected') : t('observability.values.disconnected')} />
                    <Stat label={t('observability.metrics.mqttReconnectAttempts')} value={formatMetric(snapshot.runtime.mqttReconnectAttempts)} />
                    <Stat label={t('observability.metrics.mqttReceived')} value={formatMetric(snapshot.runtime.mqttReceived)} />
                    <Stat label={t('observability.metrics.mqttEnvelopeFailed')} value={formatMetric(snapshot.runtime.mqttEnvelopeFailed)} />
                    <Stat label={t('observability.metrics.mqttDispatchAccepted')} value={formatMetric(snapshot.runtime.mqttDispatchAccepted)} />
                    <Stat label={t('observability.metrics.mqttDispatchRejected')} value={formatMetric(snapshot.runtime.mqttDispatchRejected)} />
                  </div>
                </SectionCard>

                <SectionCard title={t('observability.sections.stateUpdate')} dense>
                  <div className="ingestion-observability-stat-list">
                    <Stat label={t('observability.metrics.stateUpdateQueued')} value={formatMetric(snapshot.runtime.stateUpdateQueued)} />
                    <Stat label={t('observability.metrics.stateUpdateSucceeded')} value={formatMetric(snapshot.runtime.stateUpdateSucceeded)} />
                    <Stat label={t('observability.metrics.stateUpdateFailed')} value={formatMetric(snapshot.runtime.stateUpdateFailed)} />
                    <Stat label={t('observability.metrics.stateUpdateRetrying')} value={formatMetric(snapshot.runtime.stateUpdateRetrying)} />
                    <Stat label={t('observability.metrics.stateUpdateStaleRejected')} value={formatMetric(snapshot.runtime.stateUpdateStaleRejected)} />
                    <Stat label={t('observability.metrics.stateUpdateLagSec')} value={formatMetric(snapshot.runtime.stateUpdateLagSec)} />
                  </div>
                </SectionCard>

                <SectionCard title={t('observability.sections.enrichment')} dense>
                  <div className="ingestion-observability-stat-list">
                    <Stat label={t('observability.metrics.enrichmentSubmitted')} value={formatMetric(snapshot.runtime.enrichmentSubmitted)} />
                    <Stat label={t('observability.metrics.enrichmentRejected')} value={formatMetric(snapshot.runtime.enrichmentRejected)} />
                    <Stat label={t('observability.metrics.enrichmentCacheSize')} value={formatMetric(snapshot.runtime.enrichmentCacheSize)} />
                    <Stat label={t('observability.metrics.resendWithDifferentContent')} value={formatMetric(snapshot.runtime.resendWithDifferentContent)} />
                  </div>
                </SectionCard>

                <SectionCard title={t('observability.sections.liveness')} dense>
                  <div className="ingestion-observability-stat-list">
                    <Stat label={t('observability.metrics.livenessPingSuccess')} value={formatMetric(snapshot.runtime.livenessPingSuccess)} />
                    <Stat label={t('observability.metrics.livenessPingFailure')} value={formatMetric(snapshot.runtime.livenessPingFailure)} />
                    <Stat label={t('observability.metrics.livenessFileFallback')} value={formatMetric(snapshot.runtime.livenessFileFallback)} />
                    <Stat label={t('observability.metrics.livenessStatusTransitions')} value={formatMetric(snapshot.runtime.livenessStatusTransitions)} />
                  </div>
                </SectionCard>
              </div>

              <SectionCard title={t('observability.sections.latency')} dense>
                {latencyRows.length === 0 ? (
                  <EmptyState message={t('observability.empty.latency')} />
                ) : (
                  <div className="ingestion-observability-table-wrap">
                    <table className="ingestion-observability-table">
                      <thead>
                        <tr>
                          <th>{t('observability.tables.stage')}</th>
                          <th>{t('observability.tables.count')}</th>
                          <th>{t('observability.tables.avgMs')}</th>
                          <th>{t('observability.tables.p50Ms')}</th>
                          <th>{t('observability.tables.p95Ms')}</th>
                          <th>{t('observability.tables.maxMs')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latencyRows.map(([stageName, stage]) => (
                          <tr key={stageName}>
                            <td>{formatStageName(stageName)}</td>
                            <td>{formatMetric(stage.count)}</td>
                            <td>{stage.avgMs.toFixed(3)}</td>
                            <td>{stage.p50Ms.toFixed(3)}</td>
                            <td>{stage.p95Ms.toFixed(3)}</td>
                            <td>{stage.maxMs.toFixed(3)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>

          <div className="ingestion-observability-card-grid">
            <SectionCard title={t('observability.sections.auditTrail')} dense>
              <div className="ingestion-observability-table-toolbar">
                <TextField
                  size="small"
                  value={auditQuery.search}
                  onChange={handleSearchChange(setAuditQuery)}
                  placeholder={t('search.placeholder')}
                  className="ingestion-observability-search"
                />
                {diagnosticsLoading ? <CircularProgress size={16} /> : null}
              </div>
              {panelStates.auditRows.availability !== 'ready' ? (
                <SectionMessage state={panelStates.auditRows} />
              ) : auditPage.items.length === 0 ? (
                <EmptyState message={t('observability.empty.audit')} />
              ) : (
                <div className="ingestion-observability-table-wrap">
                  <table className="ingestion-observability-table">
                    <thead>
                      <tr>
                        <th>{t('observability.tables.createdAt')}</th>
                        <th>{t('observability.tables.event')}</th>
                        <th>{t('observability.tables.status')}</th>
                        <th>{t('observability.tables.rtuId')}</th>
                        <th>{t('observability.tables.source')}</th>
                        <th>{t('observability.tables.error')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditPage.items.map((row, index) => (
                        <tr key={`${row.occurredAtUtc}-${row.eventType}-${row.correlationId || 'none'}-${index}`}>
                          <td>{formatTimestamp(row.occurredAtUtc)}</td>
                          <td>{formatStageName(row.eventType)}</td>
                          <td>
                            <span className="ingestion-observability-chip-cell">
                              <Chip size="small" color={statusChipColor(row.outcome)} label={formatEventStatusLabel(row.outcome)} />
                            </span>
                          </td>
                          <td>{formatRtuDisplay(row.rtuId)}</td>
                          <td>
                            {row.sourceComponent ? (
                              <TruncatedCellValue value={row.sourceComponent} />
                            ) : (
                              <div>—</div>
                            )}
                            {row.correlationId ? (
                              <TruncatedCellValue
                                value={row.correlationId}
                                display={shortenIdentity(row.correlationId)}
                                className="ingestion-observability-muted"
                              />
                            ) : null}
                          </td>
                          <td>
                            {row.errorCode || row.safeReason ? (
                              <TruncatedCellValue value={(row.errorCode || row.safeReason) as string} />
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <TablePagination
                component="div"
                count={auditPage.totalElements}
                page={auditPage.page}
                onPageChange={handlePageChange(setAuditQuery)}
                rowsPerPage={auditPage.size}
                onRowsPerPageChange={handleRowsPerPageChange(setAuditQuery)}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="ingestion-observability-pagination"
              />
            </SectionCard>

            <SectionCard title={t('observability.sections.quarantine')} dense>
              <div className="ingestion-observability-table-toolbar">
                <TextField
                  size="small"
                  value={quarantineSearchInput}
                  onChange={(event) => setQuarantineSearchInput(event.target.value)}
                  placeholder={t('observability.quarantineSearchPlaceholder', {
                    defaultValue: 'Search RTU, filename, file type, serial, or correlation ID',
                  })}
                  className="ingestion-observability-search"
                  inputProps={{ 'aria-label': t('observability.sections.quarantine') }}
                />
                <div className="ingestion-observability-redrive-toolbar">
                  {selectedQuarantineCount > 0 ? (
                    <>
                      <TextField
                        size="small"
                        value={redriveReason}
                        onChange={(event) => setRedriveReason(event.target.value)}
                        placeholder={t('observability.redrive.reasonPlaceholder')}
                        className="ingestion-observability-redrive-reason"
                      />
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={redrivingIds.size > 0 ? <CircularProgress size={13} color="inherit" /> : <ReplayIcon fontSize="small" />}
                        onClick={() => void handleBulkRedrive()}
                        disabled={redrivingIds.size > 0}
                      >
                        {t('observability.redrive.selected', { count: selectedQuarantineCount })}
                      </Button>
                    </>
                  ) : null}
                  {diagnosticsLoading ? <CircularProgress size={16} /> : null}
                </div>
              </div>
              {redriveNotice ? (
                <Alert severity={redriveNotice.severity} className="ingestion-observability-redrive-notice">
                  {redriveNotice.message}
                </Alert>
              ) : null}
              {panelStates.quarantineRows.availability !== 'ready' ? (
                <SectionMessage state={panelStates.quarantineRows} />
              ) : quarantinePage.items.length === 0 ? (
                <EmptyState message={t('observability.empty.quarantine')} />
              ) : (
                <div className="ingestion-observability-table-wrap">
                  <table className="ingestion-observability-table">
                    <thead>
                      <tr>
                        <th className="ingestion-observability-select-cell">
                          <Checkbox
                            size="small"
                            checked={allVisibleQuarantineSelected}
                            indeterminate={selectedQuarantineCount > 0 && !allVisibleQuarantineSelected}
                            disabled={eligibleQuarantineIds.length === 0}
                            onChange={(event) => toggleVisibleQuarantineSelection(event.target.checked)}
                            inputProps={{ 'aria-label': 'Select visible recovery rows' }}
                          />
                        </th>
                        <th>{t('observability.tables.createdAt')}</th>
                        <th>{t('observability.tables.status')}</th>
                        <th>{t('observability.tables.rtuId')}</th>
                        <th>{t('observability.tables.fileType')}</th>
                        <th>{t('observability.tables.error')}</th>
                        <th>{t('observability.tables.download')}</th>
                        <th>{t('table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarantinePage.items.map((row, index) => (
                        <tr key={`${row.id}-${row.correlationId}-${index}`}>
                          <td className="ingestion-observability-select-cell">
                            <Checkbox
                              size="small"
                              checked={selectedQuarantineIds.has(row.id)}
                              disabled={!isQuarantineRedriveEligible(row) || redrivingIds.has(row.id)}
                              onChange={(event) => toggleQuarantineSelection(row.id, event.target.checked)}
                              inputProps={{ 'aria-label': `Select quarantine row ${row.id}` }}
                            />
                          </td>
                          <td>{formatTimestamp(row.createdAt)}</td>
                          <td>
                            <span className="ingestion-observability-chip-cell">
                              <Chip size="small" color={statusChipColor(row.status)} label={formatEventStatusLabel(row.status)} />
                            </span>
                          </td>
                          <td>
                            {(() => {
                              const identity = formatQuarantineIdentity(row);
                              return identity.fromFilename ? (
                                <span
                                  className="ingestion-observability-filename-identity"
                                  title={t('observability.tables.rtuIdFromFilenameTooltip')}
                                >
                                  {identity.display}
                                </span>
                              ) : (
                                identity.display
                              );
                            })()}
                          </td>
                          <td>{row.fileType || t('observability.values.unknown')}</td>
                          <td>
                            {row.errorCode ? (
                              <TruncatedCellValue value={row.errorCode} />
                            ) : (
                              <div>—</div>
                            )}
                            {row.safeReason ? (
                              <TruncatedCellValue
                                value={row.safeReason}
                                className="ingestion-observability-muted"
                              />
                            ) : null}
                          </td>
                          <td>
                            {row.downloadAvailable ? (
                              <Button
                                size="small"
                                variant="text"
                                startIcon={downloadingCorrelationId === row.correlationId ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                                onClick={() => void handleDownload(row.correlationId)}
                                disabled={downloadingCorrelationId === row.correlationId}
                              >
                                {t('observability.actions.downloadFile')}
                              </Button>
                            ) : (
                              <Typography variant="body2" className="ingestion-observability-muted">—</Typography>
                            )}
                          </td>
                          <td>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={redrivingIds.has(row.id) ? <CircularProgress size={13} /> : <ReplayIcon fontSize="small" />}
                              onClick={() => void handleSingleRedrive(row)}
                              disabled={!isQuarantineRedriveEligible(row) || redrivingIds.has(row.id)}
                            >
                              {t('observability.redrive.single')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <TablePagination
                component="div"
                count={quarantinePage.totalElements}
                page={quarantinePage.page}
                onPageChange={handlePageChange(setQuarantineQuery)}
                rowsPerPage={quarantinePage.size}
                onRowsPerPageChange={handleRowsPerPageChange(setQuarantineQuery)}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="ingestion-observability-pagination"
              />
            </SectionCard>
          </div>
                </>
              ) : null}
            </div>
          ) : null}
        </>
    </div>
  );
};

export default IngestionObservabilityPage;
